import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Button, Modal } from 'react-bootstrap';

/*
 * Anteprima dei file in una finestra (set-ui/regole/anteprima-file.md): un PDF o un'immagine non si fa
 * scaricare per vederlo, si apre qui dentro. Gli altri formati (XML, fogli di calcolo, archivi…) restano un download.
 *
 *   apriFile({ uri, nome })                 // anteprima se PDF/immagine, altrimenti si apre/scarica come prima
 *   anteprimaFile({ uri, nome })            // sempre la finestra
 *   anteprimaFile({ blob, nome })           // file gia' scaricato in memoria (es. fattura PDF dal server)
 *   eAnteprimabile({ nome, uri, tipo })     // true per PDF e immagini
 */
/*
 * Un PDF su un altro indirizzo si apre diretto solo se l'indirizzo firmato chiede a S3 di servirlo come PDF da mostrare:
 * il backend lo fa per ogni file con nome .pdf («modifiche 12 ottobre», punto 6), e cosi' l'indirizzo non esce dall'app.
 * Gli altri (file senza estensione, indirizzi non firmati dal backend) S3 li servirebbe come `application/octet-stream`
 * e il browser li scaricherebbe: per quelli resta il visualizzatore di Google.
 */
const eRemoto = (uri) => {
    try { return /^https?:/i.test(uri) && new URL(uri).origin !== window.location.origin; } catch (e) { return false; }
};

const servitoComePdf = (uri) => {
    try {
        const parametri = new URL(uri).searchParams;
        return parametri.get('response-content-type') === 'application/pdf' && parametri.get('response-content-disposition') === 'inline';
    } catch (e) { return false; }
};

const indirizzoPdf = (uri) => (eRemoto(uri) && !servitoComePdf(uri) ? `https://docs.google.com/gview?url=${encodeURIComponent(uri)}&embedded=true` : uri);

const IMMAGINI = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'heic'];

const estensione = (testo = '') => {
    const pulito = String(testo).split('#')[0].split('?')[0];
    const punto = pulito.lastIndexOf('.');
    return punto >= 0 ? pulito.slice(punto + 1).toLowerCase() : '';
};

// 'pdf' | 'immagine' | null, dal tipo MIME oppure dall'estensione del nome o dell'indirizzo
export const tipoAnteprima = ({ nome = '', uri = '', tipo = '' } = {}) => {
    const mime = String(tipo || '').toLowerCase();
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('image/')) return 'immagine';
    const ext = estensione(nome) || estensione(uri);
    if (ext === 'pdf') return 'pdf';
    if (IMMAGINI.includes(ext)) return 'immagine';
    return null;
};

export const eAnteprimabile = (file) => tipoAnteprima(file) !== null;

const Anteprima = ({ uri, nome, tipoFile, scarica, chiudi }) => {
    const [mostra, setMostra] = useState(true);
    const [errore, setErrore] = useState(false);
    // Tipo non riconoscibile da nome e indirizzo (es. un indirizzo firmato senza estensione): si prova come immagine e, se non lo è, come PDF
    const [tipoMostrato, setTipoMostrato] = useState(tipoFile || 'immagine');
    useEffect(() => { setErrore(false); setTipoMostrato(tipoFile || 'immagine'); }, [uri, tipoFile]);
    const titolo = nome || (tipoMostrato === 'pdf' ? 'Documento' : 'Immagine');
    return (
        <Modal show={mostra} centered size="xl" className="sid-anteprima" onHide={() => setMostra(false)} onExited={chiudi}>
            <Modal.Header closeButton><Modal.Title>{titolo}</Modal.Title></Modal.Header>
            <Modal.Body>
                {errore ? (
                    <p className="text-center my-5">Non riesco a mostrare l&apos;anteprima di questo file.</p>
                ) : tipoMostrato === 'pdf' ? (
                    <iframe
                        className="sid-anteprima__pdf"
                        title={titolo}
                        src={indirizzoPdf(uri)}
                    />
                ) : (
                    <img className="sid-anteprima__img" alt={titolo} src={uri} onError={() => (tipoFile ? setErrore(true) : setTipoMostrato('pdf'))} />
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
                {scarica && (
                    <Button as="a" href={uri} download={nome || undefined} variant="outline-dark" size="sm" className="sid-btn-compact">Scarica</Button>
                )}
                <Button className="btn btn-primary btn-add" onClick={() => setMostra(false)}>Chiudi</Button>
            </Modal.Footer>
        </Modal>
    );
};

export const anteprimaFile = ({ uri = '', blob = null, nome = '', tipo = '', scarica = true } = {}) => {
    const indirizzo = blob ? URL.createObjectURL(blob) : uri;
    const tipoFile = tipoAnteprima({ nome, uri: blob ? '' : uri, tipo: tipo || blob?.type }) || (blob ? 'pdf' : null);
    const nodo = document.createElement('div');
    document.body.appendChild(nodo);
    const root = createRoot(nodo);
    const chiudi = () => {
        root.unmount();
        nodo.remove();
        if (blob) URL.revokeObjectURL(indirizzo);
    };
    root.render(<Anteprima uri={indirizzo} nome={nome} tipoFile={tipoFile} scarica={scarica} chiudi={chiudi} />);
};

// PDF e immagini nella finestra di anteprima; gli altri file come prima (nuova scheda / download)
export const apriFile = ({ uri = '', nome = '', tipo = '', scarica = true } = {}) => {
    if (!uri) return;
    if (eAnteprimabile({ nome, uri, tipo })) {
        anteprimaFile({ uri, nome, tipo, scarica });
        return;
    }
    window.open(uri, '_blank', 'noopener,noreferrer');
};

export default apriFile;
